import { useCallback, useEffect, useRef, useState } from 'react';

// Two-phase close for full-screen mobile overlays that own the keyboard.
//
// With Capacitor `Keyboard.resize: 'native'` the webview is SHORTER while the
// keyboard is up. Unmounting the overlay on Cancel therefore revealed the
// page underneath at keyboard height (step 1), and only then did the keyboard
// slide away and the webview grow back (step 2) — a visible two-step
// "minimize". Instead: blur the input (starts the keyboard dismissal), keep
// the overlay mounted until the keyboard has actually gone (Capacitor's
// `keyboardDidHide` window event — Android's slide-down is ~400ms and a fixed
// timer was always wrong on one platform), then fade the overlay out and call
// onClose. The webview resize happens behind the opaque overlay.
//
// Returns { closing, fading, requestClose }. Render `is-closing` from
// `closing` (blocks taps, stays opaque) and `is-fading` from `fading` (the
// short opacity transition right before unmount); call requestClose()
// wherever onClose used to be called.
const FADE_MS = 160;            // matches .mobile-search-overlay.is-closing
const KEYBOARD_HIDE_MAX_MS = 700; // safety net if keyboardDidHide never fires

// Module-level keyboard visibility, fed by the Capacitor Keyboard plugin's
// window events (fired on iOS + Android; never on web/desktop, where it
// simply stays false and close is immediate).
let keyboardVisible = false;
if (typeof window !== 'undefined') {
    window.addEventListener('keyboardWillShow', () => { keyboardVisible = true; });
    window.addEventListener('keyboardDidShow', () => { keyboardVisible = true; });
    window.addEventListener('keyboardDidHide', () => { keyboardVisible = false; });
}

function whenKeyboardHidden() {
    if (!keyboardVisible) return Promise.resolve();
    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            window.removeEventListener('keyboardDidHide', finish);
            clearTimeout(t);
            resolve();
        };
        const t = setTimeout(finish, KEYBOARD_HIDE_MAX_MS);
        window.addEventListener('keyboardDidHide', finish);
    });
}

export default function useOverlayDismiss(open, onClose, inputRef) {
    const [closing, setClosing] = useState(false);
    const [fading, setFading] = useState(false);
    const pendingRef = useRef(false);
    const timerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Any reopen cancels a pending close.
    useEffect(() => {
        if (open) return undefined;
        clearTimeout(timerRef.current);
        pendingRef.current = false;
        setClosing(false);
        setFading(false);
        return undefined;
    }, [open]);

    useEffect(() => () => { clearTimeout(timerRef.current); pendingRef.current = false; }, []);

    const requestClose = useCallback(() => {
        if (pendingRef.current) return; // already closing
        pendingRef.current = true;
        inputRef?.current?.blur();
        // Keep taps from landing on the overlay while it waits (it stays
        // fully opaque until the keyboard is gone, then fades).
        setClosing(true);
        whenKeyboardHidden().then(() => {
            if (!pendingRef.current) return;
            setFading(true);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                pendingRef.current = false;
                setClosing(false);
                setFading(false);
                onCloseRef.current && onCloseRef.current();
            }, FADE_MS);
        });
    }, [inputRef]);

    return { closing, fading, requestClose };
}
